import MuxPlayer from '@mux/mux-player-react'
import { Link } from '@tanstack/react-router'
import { trpc } from '#lib/trpc'

function formatDuration(seconds: number) {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return s === 0 ? `${m}m` : `${m}m ${s}s`
}

export function AdminClipDetailPage({ clipId }: { clipId: string }) {
  const { data: clip, isLoading: clipLoading } = trpc.clips.byId.useQuery({ id: clipId })
  const { data: playbackId, isLoading: playbackLoading } = trpc.clips.getPlaybackId.useQuery(
    { id: clipId },
    { enabled: !!clip }
  )

  if (clipLoading) {
    return <p className="text-warm-gray-400 text-sm">Loading…</p>
  }

  if (!clip) {
    return <p className="text-warm-gray-400 text-sm">Clip not found.</p>
  }

  return (
    <div className="flex flex-col gap-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <Link
          to="/admin/clips"
          className="text-sm text-warm-gray-400 hover:text-warm-gray-600 transition-colors"
        >
          ← Clips
        </Link>
      </div>

      <div>
        <h1 className="font-serif text-3xl text-warm-gray-900">{clip.name}</h1>
        {clip.description && <p className="text-warm-gray-500 mt-1">{clip.description}</p>}
      </div>

      <div className="rounded-2xl overflow-hidden bg-black aspect-video flex items-center justify-center">
        {playbackLoading ? (
          <p className="text-white/40 text-sm">Loading player…</p>
        ) : playbackId ? (
          <MuxPlayer
            playbackId={playbackId}
            className="w-full h-full"
            style={{ aspectRatio: '16/9' }}
          />
        ) : (
          <p className="text-white/40 text-sm">Player not available — asset still processing?</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 text-sm">
        <div className="flex flex-col gap-1">
          <span className="text-xs text-warm-gray-400 uppercase tracking-wide">Duration</span>
          <span className="text-warm-gray-800 font-medium">
            {formatDuration(clip.durationSeconds)}
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-warm-gray-400 uppercase tracking-wide">Intensity</span>
          <span className="text-warm-gray-800 font-medium capitalize">{clip.intensity}</span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-warm-gray-400 uppercase tracking-wide">Phases</span>
          <div className="flex flex-wrap gap-1">
            {clip.phases.map((p) => (
              <span
                key={p}
                className="px-2 py-0.5 rounded-full bg-sage-400/10 text-sage-700 text-xs capitalize"
              >
                {p}
              </span>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-warm-gray-400 uppercase tracking-wide">Mux Asset ID</span>
          <span className="text-warm-gray-500 text-xs font-mono truncate">{clip.muxAssetId}</span>
        </div>
      </div>

      {clip.goalTags.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-xs text-warm-gray-400 uppercase tracking-wide">Goal tags</span>
          <div className="flex flex-wrap gap-2">
            {clip.goalTags.map((tag) => (
              <span
                key={tag}
                className="px-2 py-1 rounded-full bg-terracotta-400/10 text-terracotta-700 text-xs"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
